import urls from "../../urls";
import axios from "axios";
import { getFirebaseIDToken } from "../firebase/firebase";

export const sendOrderToExact = (order, token) => {
  return new Promise(function (resolve, reject) {
    const requestOptions = {
      method: "POST",
      headers: { Authorization: token, "Content-Type": "application/json" },
      body: JSON.stringify(order),
    };
    fetch(urls.exactOrder, requestOptions)
      .then((response) => {
        if (!response.ok) {
          response.text().then((text) => reject({ code: null, message: text }));
        } else resolve();
      })
      .catch((error) => {
        if (typeof error.json === "function") {
          error
            .json()
            .then((jsonError) => {
              reject({ code: null, message: jsonError });
            })
            .catch((genericError) => {
              reject({ code: null, message: genericError });
            });
        } else {
          reject({ code: 503, message: "Fetch error" });
        }
      });
  });
};

// Configuratie: welke product keys moeten naar Exact gesynchroniseerd worden
const EXACT_SYNC_KEYS = ["barcode"];
// const EXACT_SYNC_KEYS = ["name", "code", "barcode", "description", "price", "purchasePrice", "stock", "taxRate"];

/**
 * Map product data naar Exact object formaat
 * @param {Object} product - Het product object met onze keys
 * @param {Array} keysToSync - Array van keys die gesynchroniseerd moeten worden
 * @returns {Object} - Object met Exact key names
 */
export const mapProductToExactObject = (product, keysToSync = EXACT_SYNC_KEYS) => {
  const exactMapping = {
    name: "Description",
    code: "Code",
    barcode: "Barcode",
    description: "Notes",
    price: "SalesPrice",
    purchasePrice: "CostPrice",
    stock: "Stock",
    taxRate: "VATCode",
  };

  const exactObject = {};

  keysToSync.forEach((key) => {
    if (exactMapping[key] && product[key] !== undefined) {
      exactObject[exactMapping[key]] = product[key];
    }
  });

  return exactObject;
};

/**
 * Stuur product updates naar Exact API
 * @param {Object} productData - De product data die gesynchroniseerd moet worden
 * @param {String} exactGuid - Het Exact Product ID
 * @param {Array} keysToSync - Optioneel: specifieke keys om te synchroniseren
 * @returns {Promise} - Resolves met { data, syncedKeys }
 */
export const syncProductToExact = (productData, exactGuid, keysToSync = EXACT_SYNC_KEYS) => {
  return new Promise(async (resolve, reject) => {
    const exactData = mapProductToExactObject(productData, keysToSync);

    const exactProductUrl = process.env.REACT_APP_API_URL + "/api/exact/item/" + exactGuid;
    const idToken = await getFirebaseIDToken();

    const options = {
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
    };

    // Bepaal welke keys daadwerkelijk gesynchroniseerd worden
    const syncedKeys = keysToSync.filter(
      (key) => typeof productData[key] !== undefined && productData[key] !== "" && productData[key] !== null,
    );

    // Als geen van de values een waarde hebben (of leeg zijn) hoeft het niet verstuurd te worden
    if (syncedKeys.length === 0) {
      resolve({ data: null, syncedKeys: null });
    }

    axios
      .put(exactProductUrl, exactData, options)
      .then((response) => {
        resolve({ data: response.data, syncedKeys });
      })
      .catch((error) => {
        if (error.response) {
          reject({ code: error.response.status, message: error.response.data });
        } else {
          reject({ code: 503, message: "Network error" });
        }
      });
  });
};

/**
 * Haal een product op uit Exact op basis van productcode
 * @param {String} code - De productcode om op te zoeken
 * @returns {Promise} - Resolves met product data of null als niet gevonden
 */
export const getExactProduct = (code) => {
  return new Promise(async (resolve, reject) => {
    const exactProductUrl = urls.getExactItem + code;
    const idToken = await getFirebaseIDToken();

    const options = {
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
    };

    axios
      .get(exactProductUrl, options)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        if (error.response) {
          // Als 404, geen product gevonden
          if (error.response.status === 404) {
            resolve(null);
          } else {
            reject({ code: error.response.status, message: error.response.data });
          }
        } else {
          reject({ code: 503, message: "Network error" });
        }
      });
  });
};

/**
 * Verkooporders uit Exact voor de CMS-pagina Bestellingen → Exact.
 *
 * @param {Object} options
 * @param {String} [options.status] null/"running" = open + deels geleverd
 *   (standaard), "all" = alles, of een kommalijst met Exact-statuscodes.
 * @param {Number} [options.months] hoeveel maanden terug op orderdatum.
 */
export const getExactSalesOrders = async ({ status, months } = {}) => {
  const idToken = await getFirebaseIDToken();
  const params = {};
  if (status) params.status = status;
  if (months !== undefined && months !== null) params.months = months;

  const response = await axios.get(urls.getExactOrders, { headers: { Authorization: idToken }, params });
  return Array.isArray(response.data) ? response.data : [];
};

/** Eén verkooporder met alle regels, voor de detailpagina. */
export const getExactSalesOrder = async (orderId) => {
  const idToken = await getFirebaseIDToken();
  const response = await axios.get(urls.getExactOrder + encodeURIComponent(orderId), {
    headers: { Authorization: idToken },
  });
  return response.data;
};

/**
 * De factuur-PDF('s) bij een order, als base64-strings.
 *
 * De API zoekt de facturen op `YourRef` — dus op ónze referentie, niet op het
 * Exact-ordernummer. Een order die in Exact zelf is ingetikt zonder referentie
 * levert daarom niets op; dat is geen fout, er is dan gewoon niets te tonen.
 */
export const getExactOrderInvoices = async (yourRef) => {
  if (!yourRef) return [];
  const idToken = await getFirebaseIDToken();
  const response = await axios.get(urls.getExactOrderInvoice, {
    headers: { Authorization: idToken },
    params: { orderId: yourRef },
  });
  return Array.isArray(response.data) ? response.data : [];
};

/**
 * Naar welk adres zou de bevestiging voor deze Exact-order gaan? Verstuurt niets.
 *
 * Geeft `{ email, name, orderNumber, yourRef, linkedWebshopAccount }`. `email`
 * kan null zijn: een order van een klant zonder webshopaccount én zonder
 * e-mailadres in Exact. Dan moet er in het CMS zelf een adres ingevuld worden.
 */
export const getExactOrderConfirmationRecipient = async (orderId) => {
  const idToken = await getFirebaseIDToken();
  const response = await axios.get(urls.exactOrderConfirmation(orderId), {
    headers: { Authorization: idToken },
  });
  return response.data;
};

/**
 * Stuurt de orderbevestiging voor een Exact-order naar de klant.
 *
 * Dezelfde mail als de gewone orderbevestiging, maar opgebouwd uit wat er in
 * Exact staat. `email` leeg laten betekent "het adres dat bij de klant hoort".
 */
export const sendExactOrderConfirmation = async (orderId, email) => {
  const idToken = await getFirebaseIDToken();
  const response = await axios.post(
    urls.exactOrderConfirmation(orderId),
    { email: email || null },
    { headers: { Authorization: idToken, "Content-Type": "application/json" } },
  );
  return response.data;
};
